/**
 * SearchAgentService — Recherche intelligente du catalogue (langue naturelle FR/EN).
 *
 * 8 actions :
 *   1. parseSearchQuery           — extrait filtres (cat/couleurs/marques/prix/etc.)
 *   2. searchCatalog              — filtre + tri par pertinence
 *   3. correctTypo                — corrige les fautes via dictionnaire + Levenshtein ≤2
 *   4. suggestAutocomplete        — suggestions live pendant la saisie
 *   5. extractFiltersFromQuery    — alias léger de parseSearchQuery
 *   6. getTrendingSearches        — top recherches hardcodées (MVP)
 *   7. saveSearch                 — sauvegarde locale d'une recherche favorite
 *   8. getRelatedSearches         — recherches similaires depuis l'historique
 *
 * Pure JS, déterministe. Pas d'appel IA payant.
 *
 * v1.12.255
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Dictionnaires FR + EN ──────────────────────────────────────────────
const CATEGORY_KEYWORDS = {
  Robes: [
    'robe', 'robes', 'dress', 'dresses', 'jumpsuit', 'jumpsuits',
    'combinaison', 'combinaisons', 'salopette', 'salopettes', 'playsuit',
  ],
  Manteaux: [
    'manteau', 'manteaux', 'trench', 'trenchs', 'caban', 'cabans',
    'doudoune', 'doudounes', 'parka', 'parkas', 'pardessus',
    'duffle', 'duffle coat', 'cape', 'capes', 'overcoat', 'overcoats',
    'peacoat', 'peacoats', 'topcoat', 'raincoat', 'raincoats', 'imperméable', 'impermeables',
  ],
  Vestes: [
    'veste', 'vestes', 'blazer', 'blazers', 'bomber', 'bombers',
    'perfecto', 'perfectos', 'coupe-vent', 'coupe vent', 'teddy', 'teddys',
    'cardigan', 'cardigans', 'jacket', 'jackets', 'biker', 'bikers',
    'kimono', 'kimonos',
  ],
  Hauts: [
    't-shirt', 't shirt', 'tshirt', 'tshirts', 't-shirts', 'tee', 'tees',
    'chemise', 'chemises', 'chemisier', 'chemisiers', 'blouse', 'blouses',
    'polo', 'polos', 'pull', 'pulls', 'pullover', 'pullovers', 'sweater', 'sweaters',
    'cardigan', 'cardigans', 'hoodie', 'hoodies', 'sweat', 'sweats', 'sweatshirt',
    'débardeur', 'debardeur', 'tank', 'top', 'tops', 'bustier', 'bustiers',
    'crop top', 'crop-top', 'tunique', 'tuniques', 'shirt', 'shirts',
  ],
  Bas: [
    'jean', 'jeans', 'pantalon', 'pantalons', 'short', 'shorts',
    'jupe', 'jupes', 'legging', 'leggings', 'jogging', 'joggings',
    'bermuda', 'bermudas', 'chino', 'chinos', 'culotte', 'culottes',
    'pants', 'trousers', 'skirt', 'skirts', 'joggers', 'sweatpants',
  ],
  // v1.12.294 — Maillots de bain (homme + femme)
  Maillots: [
    'maillot', 'maillots', 'maillot de bain', 'maillots de bain',
    'bikini', 'bikinis', 'monokini', 'tankini', 'trikini',
    'swimsuit', 'swim suit', 'swimsuits', 'swimwear', 'swimming',
    'swim shorts', 'swimshorts', 'swim short',
    'swim trunk', 'swim trunks', 'swimtrunk', 'swimtrunks',
    'swim brief', 'swim briefs',
    'board shorts', 'boardshorts', 'board short',
    'slip de bain', 'short de bain', 'burkini',
    'plage', 'piscine', 'beach', 'pool',
  ],
  Chaussures: [
    'chaussure', 'chaussures', 'shoes', 'shoe', 'sneakers', 'sneaker', 'baskets', 'basket',
    'mocassins', 'mocassin', 'derbies', 'derby', 'escarpins', 'escarpin',
    'sandales', 'sandale', 'bottes', 'botte', 'bottines', 'bottine',
    'ballerines', 'ballerine', 'tongs', 'tong', 'espadrilles', 'espadrille',
    'tennis', 'loafers', 'loafer', 'oxfords', 'oxford', 'boots', 'boot',
    'pumps', 'pump', 'heels', 'heel', 'mules', 'chaussons',
  ],
  Sacs: [
    'sac', 'sacs', 'sac à main', 'sac a main', 'cabas', 'sac à dos', 'sac a dos',
    'besace', 'besaces', 'mallette', 'mallettes', 'pochette', 'pochettes',
    'sacoche', 'sacoches', 'clutch', 'clutches', 'bag', 'bags',
    'handbag', 'handbags', 'tote', 'totes', 'backpack', 'backpacks',
    'crossbody', 'messenger', 'wallet', 'wallets',
  ],
  Bijoux: [
    'bijou', 'bijoux', 'collier', 'colliers', 'bracelet', 'bracelets',
    'bague', 'bagues', 'boucle d\'oreille', 'boucles d\'oreille', 'pendentif', 'pendentifs',
    'chaîne', 'chaine', 'chaînes', 'chaines', 'jewelry', 'necklace', 'necklaces',
    'ring', 'rings', 'earring', 'earrings',
  ],
  Accessoires: [
    'accessoire', 'accessoires', 'montre', 'montres', 'lunettes', 'lunette',
    'ceinture', 'ceintures', 'chapeau', 'chapeaux', 'casquette', 'casquettes',
    'écharpe', 'echarpe', 'écharpes', 'echarpes', 'cravate', 'cravates',
    'gants', 'gant', 'foulard', 'foulards', 'bonnet', 'bonnets',
    'watch', 'watches', 'belt', 'belts', 'hat', 'hats', 'cap', 'caps',
    'scarf', 'scarves', 'tie', 'ties', 'gloves', 'glove',
  ],
};

const COLOR_KEYWORDS = {
  'noir':       ['noir', 'noire', 'noirs', 'noires', 'black'],
  'blanc':      ['blanc', 'blanche', 'blancs', 'blanches', 'white', 'écru', 'ecru', 'ivoire'],
  'gris':       ['gris', 'grise', 'grises', 'grey', 'gray', 'anthracite'],
  'beige':      ['beige', 'beiges', 'sable', 'tan'],
  'marron':     ['marron', 'marrons', 'brown', 'chocolat'],
  'camel':      ['camel'],
  'kaki':       ['kaki', 'khaki'],
  'vert':       ['vert', 'verte', 'verts', 'vertes', 'green', 'olive', 'sapin'],
  'bleu':       ['bleu', 'bleue', 'bleus', 'bleues', 'blue'],
  'marine':     ['marine', 'navy', 'bleu marine', 'bleu navy', 'navy blue'],
  'bleu ciel':  ['bleu ciel', 'sky blue', 'sky'],
  'rouge':      ['rouge', 'rouges', 'red'],
  'bordeaux':   ['bordeaux', 'burgundy', 'wine'],
  'rose':       ['rose', 'roses', 'pink'],
  'fuchsia':    ['fuchsia'],
  'violet':     ['violet', 'violette', 'violets', 'violettes', 'purple', 'mauve'],
  'jaune':      ['jaune', 'jaunes', 'yellow'],
  'orange':     ['orange', 'oranges'],
  'doré':       ['doré', 'doree', 'dorée', 'doré', 'gold', 'doree'],
  'argenté':    ['argenté', 'argentee', 'argentée', 'silver'],
  'multicolore':['multicolore', 'multicolor', 'imprimé', 'imprime', 'printed'],
  'fleurs':     ['fleurs', 'fleuri', 'fleurie', 'floral'],
  'rayures':    ['rayures', 'rayé', 'raye', 'rayée', 'striped', 'stripe'],
  'carreaux':   ['carreaux', 'carreau', 'check', 'checked', 'tartan', 'plaid'],
  'leopard':    ['leopard', 'léopard', 'animal', 'animal print'],
};

const OCCASION_KEYWORDS = {
  'travail':    ['travail', 'bureau', 'business', 'office', 'work', 'professionnel'],
  'casual':     ['casual', 'décontracté', 'decontracte', 'décontractée', 'tous les jours'],
  'weekend':    ['weekend', 'week-end', 'week end'],
  'brunch':     ['brunch', 'café'],
  'soirée':     ['soirée', 'soiree', 'soir', 'evening', 'cocktail', 'sortie', 'party', 'événement', 'evenement'],
  'cérémonie':  ['cérémonie', 'ceremonie', 'mariage', 'wedding', 'ceremony', 'baptême', 'bapteme'],
  'sport':      ['sport', 'sportif', 'sportive', 'salle de sport', 'gym', 'running', 'fitness', 'yoga', 'jogging'],
  'plage':      ['plage', 'beach', 'piscine', 'pool'],
  'vacances':   ['vacances', 'vacation', 'holiday', 'voyage', 'travel', 'trip'],
  'date':       ['date', 'rendez-vous', 'rendez vous', 'romantique'],
};

const STYLE_KEYWORDS = {
  'chic':         ['chic', 'élégant', 'elegant', 'élégante', 'elegante'],
  'smart_casual': ['smart casual', 'smart-casual'],
  'casual':       ['casual', 'décontracté', 'decontracte'],
  'sportswear':   ['sportswear', 'sport wear', 'athleisure'],
  'streetwear':   ['streetwear', 'street wear', 'urban', 'street'],
  'bohème':       ['bohème', 'boheme', 'bohem', 'boho'],
  'romantique':   ['romantique', 'romantic', 'féminin', 'feminin', 'feminine'],
  'vintage':      ['vintage', 'rétro', 'retro'],
  'classique':    ['classique', 'classic', 'intemporel', 'timeless'],
  'tendance':     ['tendance', 'trendy', 'à la mode'],
  'minimaliste':  ['minimaliste', 'minimalist', 'minimal'],
  'rock':         ['rock', 'edgy', 'gothique', 'gothic'],
  'glamour':      ['glamour', 'glam', 'sexy'],
  'preppy':       ['preppy', 'classique chic'],
};

const MATERIAL_KEYWORDS = {
  'coton':      ['coton', 'cotton'],
  'lin':        ['lin', 'linen'],
  'soie':       ['soie', 'silk'],
  'laine':      ['laine', 'wool'],
  'cachemire':  ['cachemire', 'cashmere'],
  'cuir':       ['cuir', 'leather'],
  'daim':       ['daim', 'suède', 'suede'],
  'denim':      ['jean', 'jeans', 'denim'],
  'polyester':  ['polyester'],
  'viscose':    ['viscose'],
  'satin':      ['satin', 'satinée', 'satine'],
  'velours':    ['velours', 'velvet'],
  'dentelle':   ['dentelle', 'lace'],
  'fourrure':   ['fourrure', 'fur'],
};

const SEASON_KEYWORDS = {
  'printemps': ['printemps', 'spring'],
  'été':       ['été', 'ete', 'estival', 'estivale', 'summer'],
  'automne':   ['automne', 'fall', 'autumn'],
  'hiver':     ['hiver', 'winter', 'd\'hiver'],
};

// Marques connues (premium / luxe / fashion / mid-market / FR premium)
const KNOWN_BRANDS = [
  // Luxe
  'Chanel', 'Dior', 'Louis Vuitton', 'LV', 'Gucci', 'Prada', 'Hermès', 'Hermes',
  'Saint Laurent', 'YSL', 'Balenciaga', 'Bottega Veneta', 'Givenchy', 'Valentino',
  'Loewe', 'Celine', 'Céline', 'Fendi', 'Versace', 'Goyard', 'Cartier', 'Rolex',
  // Premium
  'Hugo Boss', 'Tommy Hilfiger', 'Calvin Klein', 'Lacoste', 'Ralph Lauren',
  'Michael Kors', 'Coach', 'Burberry', 'Moncler',
  // Fashion / contemporary
  'Pinko', 'Liu Jo', 'Karl Lagerfeld', 'Diesel', 'Armani', 'Marc Jacobs', 'Ted Baker',
  // Mid-market
  "Levi's", 'Lee', 'Wrangler', 'Tommy Jeans', 'Pepe Jeans', 'Replay',
  // FR Premium
  'Sandro', 'Maje', 'Ba&sh', 'Ba & sh', 'The Kooples', 'Zadig & Voltaire', 'Claudie Pierlot',
  'Iro', 'Ami', 'Sezane', 'Sézane',
];

const LUXURY_BRANDS_LOWER = new Set([
  'chanel', 'dior', 'louis vuitton', 'lv', 'gucci', 'prada', 'hermès', 'hermes',
  'saint laurent', 'ysl', 'balenciaga', 'bottega veneta', 'givenchy', 'valentino',
  'loewe', 'celine', 'céline', 'fendi', 'versace', 'goyard', 'cartier', 'rolex',
  'burberry', 'moncler',
]);

// ─── Helpers ─────────────────────────────────────────────────────────────
function lc(s) { return String(s || '').toLowerCase().trim(); }
function normalize(s) {
  return lc(s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // remove accents
    .replace(/[^a-z0-9\s\-]/g, ' ')
    .replace(/\s+/g, ' ').trim();
}
function detectLocale(query) {
  const q = lc(query);
  // Heuristic : présence de mots français/anglais clairs
  const frHints = /\b(robe|chaussure|sac|veste|jupe|pour|avec|moins de|sous|jusqu)\b/;
  const enHints = /\b(dress|shoes|bag|jacket|skirt|for|with|less than|under|up to)\b/;
  if (frHints.test(q)) return 'fr';
  if (enHints.test(q)) return 'en';
  return 'fr'; // default
}

// Levenshtein distance (pour typos)
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 3) return 99; // shortcut
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

// Tokens utilisés pour suggestions et typos
const ALL_TOKENS = (() => {
  const set = new Set();
  for (const dict of [CATEGORY_KEYWORDS, COLOR_KEYWORDS, OCCASION_KEYWORDS, STYLE_KEYWORDS, MATERIAL_KEYWORDS, SEASON_KEYWORDS]) {
    for (const arr of Object.values(dict)) for (const w of arr) set.add(normalize(w));
  }
  for (const b of KNOWN_BRANDS) set.add(normalize(b));
  return Array.from(set);
})();

// ─── 3. CORRECT_TYPO ─────────────────────────────────────────────────────
export function correctTypo(query) {
  const original = query;
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  const corrections = [];
  const correctedTokens = tokens.map(tok => {
    if (tok.length < 4) return tok; // skip short
    if (ALL_TOKENS.includes(tok)) return tok;
    // chercher distance ≤ 2 dans ALL_TOKENS
    let best = null, bestDist = 99;
    for (const t of ALL_TOKENS) {
      if (Math.abs(t.length - tok.length) > 2) continue;
      const d = levenshtein(tok, t);
      if (d < bestDist) { bestDist = d; best = t; if (d === 1) break; }
    }
    if (best && bestDist <= 2) {
      corrections.push({ original_word: tok, corrected_word: best, distance: bestDist });
      return best;
    }
    return tok;
  });
  const corrected = correctedTokens.join(' ');
  return {
    success: true,
    action: 'CORRECT_TYPO',
    data: {
      original,
      corrected,
      confidence: corrections.length > 0 ? 0.9 : 1.0,
      corrections,
      display_message: corrections.length > 0 ? `Voulez-vous dire "${corrected}" ?` : null,
    },
  };
}

// ─── 1. PARSE_SEARCH_QUERY ───────────────────────────────────────────────
function findKeywordsInDict(text, dict) {
  const found = [];
  for (const [key, kws] of Object.entries(dict)) {
    for (const kw of kws) {
      const re = new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (re.test(text)) { found.push(key); break; }
    }
  }
  return found;
}

function extractPriceRange(text) {
  // Patterns FR/EN
  const patternsMax = [
    /(?:moins\s+de|sous|jusqu'?\s*à|maximum|max|under|less\s+than|below|<\s*)\s*(\d{1,5})\s*(?:€|euros?|eur)?/i,
    /(\d{1,5})\s*€\s*(?:max|maximum)/i,
  ];
  const patternsMin = [
    /(?:plus\s+de|au\s+dessus\s+de|over|more\s+than|above|>\s*)\s*(\d{1,5})\s*(?:€|euros?|eur)?/i,
  ];
  const patternsRange = [
    /entre\s+(\d{1,5})\s*(?:€|euros?)?\s+et\s+(\d{1,5})\s*(?:€|euros?)?/i,
    /from\s+(\d{1,5})\s+to\s+(\d{1,5})/i,
  ];
  let min = null, max = null;
  for (const p of patternsRange) {
    const m = text.match(p);
    if (m) { min = +m[1]; max = +m[2]; return { min, max }; }
  }
  for (const p of patternsMax) {
    const m = text.match(p);
    if (m) { max = +m[1]; break; }
  }
  for (const p of patternsMin) {
    const m = text.match(p);
    if (m) { min = +m[1]; break; }
  }
  return { min, max };
}

function extractBrands(text) {
  const found = [];
  for (const b of KNOWN_BRANDS) {
    const re = new RegExp(`\\b${b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(text)) found.push(b);
  }
  return found;
}

function extractSizes(text) {
  const found = new Set();
  // FR femme : 32,34,...,48 / FR homme : XS,S,M,L,XL,XXL / Chaussures : 35-46
  const reSizeFR = /\b(?:taille\s+|size\s+)?(3[2-9]|4[0-8])\b/gi;
  let m;
  while ((m = reSizeFR.exec(text)) !== null) found.add(m[1]);
  const reLetter = /\b(taille\s+|size\s+)(XXS|XS|S|M|L|XL|XXL|XXXL)\b/gi;
  while ((m = reLetter.exec(text)) !== null) found.add(m[2].toUpperCase());
  // jeans 26-40
  const reJean = /\bjean\s+(\d{2})\b/gi;
  while ((m = reJean.exec(text)) !== null) found.add(m[1]);
  return Array.from(found);
}

export function parseSearchQuery(input) {
  const data = input?.data || input || {};
  const original = String(data.query || '').trim();
  const userGender = data.user_gender || data.gender || null;

  if (!original) {
    return {
      success: false, action: 'PARSE_SEARCH_QUERY', data: null,
      errors: [{ code: 'QUERY_TOO_SHORT', message: 'Requête vide' }],
    };
  }
  if (original.length > 200) {
    return {
      success: false, action: 'PARSE_SEARCH_QUERY', data: null,
      errors: [{ code: 'QUERY_TOO_LONG', message: 'Requête trop longue (>200 chars)' }],
    };
  }

  const detected_locale = data.locale || detectLocale(original);
  const text = normalize(original);

  // Typo correction
  const typoResult = correctTypo(original).data;
  const correctedText = typoResult?.corrected ? normalize(typoResult.corrected) : text;
  const usedText = correctedText.length >= text.length * 0.6 ? correctedText : text;

  // Extraction
  const cats = findKeywordsInDict(usedText, CATEGORY_KEYWORDS);
  const colors = findKeywordsInDict(usedText, COLOR_KEYWORDS);
  const occasions = findKeywordsInDict(usedText, OCCASION_KEYWORDS);
  const styles = findKeywordsInDict(usedText, STYLE_KEYWORDS);
  const materials = findKeywordsInDict(usedText, MATERIAL_KEYWORDS);
  const seasons = findKeywordsInDict(usedText, SEASON_KEYWORDS);
  const brands = extractBrands(original); // brands sensibles aux casses (& accents) → original
  const { min: price_min, max: price_max } = extractPriceRange(original);
  const sizes = extractSizes(original);

  // Categorie principale (premier match prioritaire — Robes > Manteaux > etc.)
  const category = cats[0] || null;
  const subcategories = cats.length > 1 ? cats.slice(1) : [];

  // Display chips removable (couleur + emoji + label)
  const display_chips = [];
  if (category) display_chips.push({ type: 'category', value: category, label: category, removable: true });
  for (const c of colors) display_chips.push({ type: 'color', value: c, label: cap(c), removable: true });
  for (const o of occasions) display_chips.push({ type: 'occasion', value: o, label: cap(o), removable: true });
  for (const s of styles) display_chips.push({ type: 'style', value: s, label: cap(s.replace('_', ' ')), removable: true });
  for (const m of materials) display_chips.push({ type: 'material', value: m, label: cap(m), removable: true });
  for (const b of brands) display_chips.push({ type: 'brand', value: b, label: b, removable: true });
  if (price_max) display_chips.push({ type: 'price', value: { max: price_max, min: price_min || 0 }, label: `< ${price_max}€`, removable: true });
  for (const sz of sizes) display_chips.push({ type: 'size', value: sz, label: `Taille ${sz}`, removable: true });
  for (const sn of seasons) display_chips.push({ type: 'season', value: sn, label: cap(sn), removable: true });

  const intent = display_chips.length === 0 ? 'inspiration_search' : 'product_search';

  // Refinements suggérés
  const suggested_refinements = [];
  if (!brands.length) suggested_refinements.push({ label: 'Préciser une marque ?', type: 'brand_prompt' });
  if (!sizes.length && (category === 'Hauts' || category === 'Bas' || category === 'Robes' || category === 'Vestes' || category === 'Manteaux')) {
    suggested_refinements.push({ label: 'Préciser une taille ?', type: 'size_prompt' });
  }
  if (!materials.length && (category === 'Sacs' || category === 'Chaussures' || category === 'Vestes')) {
    suggested_refinements.push({ label: 'Filtrer par matière ?', type: 'material_prompt' });
  }
  if (!price_max && !price_min) {
    suggested_refinements.push({ label: 'Préciser un budget ?', type: 'price_prompt' });
  }

  return {
    success: true,
    action: 'PARSE_SEARCH_QUERY',
    data: {
      original_query: original,
      corrected_query: typoResult?.corrected || original,
      detected_locale,
      intent,
      confidence: typoResult?.corrections?.length ? typoResult.confidence : 0.95,
      typo_correction: typoResult?.corrections?.length ? typoResult : null,
      extracted_filters: {
        category,
        category_label: category,
        subcategories,
        colors,
        materials,
        occasions,
        styles,
        brands,
        price_min: price_min || 0,
        price_max: price_max || null,
        sizes,
        seasons,
        gender: userGender,
        keywords_extra: [],
      },
      display_chips,
      suggested_refinements,
    },
    errors: [],
    warnings: [],
  };
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

// ─── 5. EXTRACT_FILTERS_FROM_QUERY (alias léger) ─────────────────────────
export function extractFiltersFromQuery(query) {
  const r = parseSearchQuery({ data: { query } });
  return r?.data?.extracted_filters || {};
}

// ─── 2. SEARCH_CATALOG ───────────────────────────────────────────────────
function productMatchesFilter(product, filters) {
  const prod = {
    name: lc(product.name || ''),
    brand: lc(product.brand || ''),
    category: lc(product.category || ''),
    description: lc(product.description || ''),
    color: lc(product.color || product.ai_metadata?.primaryColor || ''),
    material: lc(product.material || product.ai_metadata?.material || ''),
    price: Number(product.price) || 0,
    sizes: (product.sizes || []).map(lc),
    gender: lc(product.ai_metadata?.gender || product.gender || ''),
  };
  const text = `${prod.name} ${prod.description} ${prod.brand}`;

  // Catégorie
  if (filters.category) {
    if (lc(prod.category) !== lc(filters.category)) {
      // Tolère si le nom contient la catégorie (ex: catalog mal taggé)
      const catKws = (CATEGORY_KEYWORDS[filters.category] || []).map(lc);
      if (!catKws.some(kw => text.includes(kw))) return false;
    }
  }
  // Genre
  if (filters.gender && filters.gender !== 'N' && prod.gender && prod.gender !== 'unisex' && prod.gender !== filters.gender) {
    return false;
  }
  // Couleurs (au moins 1 match)
  if (filters.colors?.length > 0) {
    const ok = filters.colors.some(c => prod.color.includes(lc(c)) || text.includes(lc(c)));
    if (!ok) return false;
  }
  // Marques (au moins 1)
  if (filters.brands?.length > 0) {
    const ok = filters.brands.some(b => prod.brand.includes(lc(b)));
    if (!ok) return false;
  }
  // Matières
  if (filters.materials?.length > 0) {
    const ok = filters.materials.some(m => prod.material.includes(lc(m)) || text.includes(lc(m)));
    if (!ok) return false;
  }
  // Prix max
  if (filters.price_max && prod.price > filters.price_max) return false;
  if (filters.price_min && prod.price < filters.price_min) return false;
  // Tailles (si demandées)
  if (filters.sizes?.length > 0 && prod.sizes.length > 0) {
    const ok = filters.sizes.some(s => prod.sizes.includes(lc(s)));
    if (!ok) return false;
  }
  return true;
}

function relevanceScore(product, filters) {
  let score = 50;
  const text = lc(product.name || '') + ' ' + lc(product.description || '');
  if (filters.category && lc(product.category) === lc(filters.category)) score += 30;
  if (filters.colors?.length > 0) {
    const c = lc(product.color || product.ai_metadata?.primaryColor || '');
    for (const col of filters.colors) if (c.includes(lc(col))) { score += 15; break; }
  }
  if (filters.brands?.length > 0) {
    const b = lc(product.brand || '');
    for (const br of filters.brands) if (b.includes(lc(br))) { score += 25; break; }
  }
  if (filters.styles?.length > 0) {
    const styleScores = product.ai_metadata?.styleScores || {};
    for (const s of filters.styles) if ((styleScores[s] || 0) >= 60) { score += 10; break; }
  }
  if (filters.occasions?.length > 0) {
    const occs = product.ai_metadata?.occasions || {};
    for (const o of filters.occasions) if ((occs[o] || 0) >= 60) { score += 10; break; }
  }
  if (product.ai_metadata?.isTrendy) score += 8;
  if (product.discount && product.discount > 20) score += 5;
  return score;
}

export function searchCatalog(input) {
  const data = input?.data || input || {};
  const filters = data.filters || {};
  const products = Array.isArray(data.available_products) ? data.available_products : [];
  const sortBy = data.sort_by || 'relevance';
  const limit = Math.max(1, Math.min(48, Number(data.limit) || 24));
  const page = Math.max(1, Number(data.page) || 1);

  let filtered = products.filter(p => p && productMatchesFilter(p, filters));

  // Sort
  if (sortBy === 'price_asc') filtered.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
  else if (sortBy === 'price_desc') filtered.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
  else if (sortBy === 'newest') filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  else {
    // relevance par défaut
    filtered.forEach(p => { p._relevanceScore = relevanceScore(p, filters); });
    filtered.sort((a, b) => (b._relevanceScore || 0) - (a._relevanceScore || 0));
  }

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const results = filtered.slice(start, start + limit);

  // Available filters dynamiques (à partir des produits filtrés)
  const brandsCount = new Map();
  const sizesCount = new Map();
  const materialsCount = new Map();
  const priceBuckets = [
    { min: 0, max: 50, count: 0 },
    { min: 50, max: 100, count: 0 },
    { min: 100, max: 200, count: 0 },
    { min: 200, max: 500, count: 0 },
    { min: 500, max: 99999, count: 0 },
  ];
  for (const p of filtered) {
    if (p.brand) brandsCount.set(p.brand, (brandsCount.get(p.brand) || 0) + 1);
    for (const s of (p.sizes || [])) sizesCount.set(s, (sizesCount.get(s) || 0) + 1);
    const mat = p.material || p.ai_metadata?.material;
    if (mat) materialsCount.set(mat, (materialsCount.get(mat) || 0) + 1);
    const pr = Number(p.price) || 0;
    for (const b of priceBuckets) { if (pr >= b.min && pr < b.max) { b.count++; break; } }
  }

  // Insights
  const insights = [];
  if (total > 0) {
    insights.push(`📊 ${total} résultat${total > 1 ? 's' : ''} trouvé${total > 1 ? 's' : ''}`);
    const prices = filtered.map(p => Number(p.price) || 0).filter(x => x > 0);
    if (prices.length > 0) {
      const avg = Math.round(prices.reduce((s, x) => s + x, 0) / prices.length);
      insights.push(`💰 Prix moyen : ${avg}€`);
    }
    if (filters.category) insights.push(`👗 Catégorie ${filters.category}`);
  } else {
    insights.push('🔍 Aucun résultat. Essayez d\'élargir vos critères.');
  }

  // Match reasons par produit (top 3 résultats)
  for (let i = 0; i < Math.min(3, results.length); i++) {
    const p = results[i];
    p._matchReasons = [];
    if (filters.category && lc(p.category) === lc(filters.category)) p._matchReasons.push(`✅ Catégorie : ${p.category}`);
    if (filters.colors?.length > 0) {
      const c = lc(p.color || p.ai_metadata?.primaryColor || '');
      const hit = filters.colors.find(col => c.includes(lc(col)));
      if (hit) p._matchReasons.push(`✅ Couleur : ${hit}`);
    }
    if (filters.brands?.length > 0) {
      const hit = filters.brands.find(b => lc(p.brand || '').includes(lc(b)));
      if (hit) p._matchReasons.push(`🏷️ Marque : ${hit}`);
    }
    if (filters.price_max && Number(p.price) <= filters.price_max) {
      p._matchReasons.push(`💰 Prix : ${p.price}€ (sous ${filters.price_max}€)`);
    }
  }

  return {
    success: true,
    action: 'SEARCH_CATALOG',
    data: {
      query: data.query || '',
      total_results: total,
      filtered_count: total,
      page,
      total_pages: totalPages,
      limit,
      results,
      available_filters: {
        brands: Array.from(brandsCount.entries()).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([name, count]) => ({ name, count })),
        sizes: Array.from(sizesCount.entries()).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([size, count]) => ({ size, count })),
        materials: Array.from(materialsCount.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([material, count]) => ({ material, count })),
        price_ranges: priceBuckets.filter(b => b.count > 0),
      },
      applied_sort: sortBy,
      insights,
    },
    errors: total === 0 ? [{ code: 'NO_RESULTS_FOUND', message: 'Aucun produit ne correspond' }] : [],
    warnings: [],
  };
}

// ─── 4. SUGGEST_AUTOCOMPLETE ─────────────────────────────────────────────
export function suggestAutocomplete({ partial, products = [], limit = 6 }) {
  const q = normalize(partial || '');
  if (q.length < 2) {
    return { success: true, action: 'SUGGEST_AUTOCOMPLETE', data: { query_partial: partial, suggestions: [] } };
  }

  // Suggestions catégories + couleurs + marques
  const suggestions = [];
  // categories
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const kw of kws) {
      const nkw = normalize(kw);
      if (nkw.startsWith(q) || nkw.includes(q)) {
        suggestions.push({ text: kw, type: 'category', icon: catIcon(cat), value: cat });
        break;
      }
    }
  }
  // brands
  for (const b of KNOWN_BRANDS) {
    if (normalize(b).startsWith(q)) {
      suggestions.push({ text: b, type: 'brand', icon: '🏷️', value: b });
    }
  }
  // colors
  for (const c of Object.keys(COLOR_KEYWORDS)) {
    if (normalize(c).startsWith(q)) {
      suggestions.push({ text: c, type: 'color', icon: '🎨', value: c });
    }
  }
  // products names (top match)
  if (products.length > 0) {
    const found = new Set();
    for (const p of products.slice(0, 200)) {
      const n = lc(p.name || '');
      if (n.includes(q) && !found.has(n)) {
        found.add(n);
        suggestions.push({ text: p.name, type: 'product', icon: '🛍️', value: p.id });
        if (found.size >= 3) break;
      }
    }
  }

  return {
    success: true,
    action: 'SUGGEST_AUTOCOMPLETE',
    data: {
      query_partial: partial,
      suggestions: suggestions.slice(0, limit),
    },
  };
}

function catIcon(cat) {
  return ({
    Robes: '👗', Hauts: '👚', Bas: '👖', Vestes: '🧥', Manteaux: '🧥',
    Chaussures: '👟', Sacs: '👜', Bijoux: '💍', Accessoires: '✨',
  })[cat] || '🛍️';
}

// ─── 6. GET_TRENDING_SEARCHES (MVP hardcoded) ─────────────────────────────
export function getTrendingSearches() {
  return {
    success: true,
    action: 'GET_TRENDING_SEARCHES',
    data: {
      period: 'this_week',
      trending: [
        { rank: 1, query: 'sac à main beige', icon: '👜' },
        { rank: 2, query: 'manteau hiver', icon: '🧥' },
        { rank: 3, query: 'sneakers blanches', icon: '👟' },
        { rank: 4, query: 'robe noire chic', icon: '👗' },
        { rank: 5, query: 'trench beige', icon: '🧥' },
      ],
    },
  };
}

// ─── 7. SAVE_SEARCH (local) ──────────────────────────────────────────────
const KEY_SAVED = (uid) => `searchagent_saved_${uid || 'guest'}`;
const KEY_HISTORY = (uid) => `searchagent_history_${uid || 'guest'}`;

export async function saveSearch({ userId, query, name }) {
  if (!userId || !query) return { success: false };
  try {
    const raw = await AsyncStorage.getItem(KEY_SAVED(userId));
    const list = raw ? (JSON.parse(raw) || []) : [];
    list.unshift({ query, name: name || query, saved_at: Date.now() });
    await AsyncStorage.setItem(KEY_SAVED(userId), JSON.stringify(list.slice(0, 30)));
    return { success: true, action: 'SAVE_SEARCH', data: { query, name } };
  } catch { return { success: false }; }
}

export async function getSavedSearches({ userId }) {
  if (!userId) return [];
  try {
    const raw = await AsyncStorage.getItem(KEY_SAVED(userId));
    return raw ? (JSON.parse(raw) || []) : [];
  } catch { return []; }
}

// ─── 8. GET_RELATED_SEARCHES ─────────────────────────────────────────────
export async function getRelatedSearches({ userId, query }) {
  // MVP : retourne 3 variantes du query (couleur swap + cat related + price hint)
  const r = parseSearchQuery({ data: { query } });
  const f = r?.data?.extracted_filters || {};
  const related = [];
  if (f.category) {
    related.push(`${f.category} pas cher`);
    related.push(`${f.category} luxe`);
  }
  if (f.colors?.length > 0) {
    const swaps = { noir: 'beige', blanc: 'noir', beige: 'noir', rose: 'blanc' };
    const swap = swaps[f.colors[0]];
    if (swap) related.push(query.replace(new RegExp(f.colors[0], 'i'), swap));
  }
  // Add saved searches
  if (userId) {
    try {
      const saved = await getSavedSearches({ userId });
      for (const s of saved.slice(0, 2)) related.push(s.query);
    } catch {}
  }
  return {
    success: true,
    action: 'GET_RELATED_SEARCHES',
    data: { related: Array.from(new Set(related)).slice(0, 5) },
  };
}

// ─── Exports utilitaires ──────────────────────────────────────────────────
export const __DICT__ = {
  CATEGORY_KEYWORDS, COLOR_KEYWORDS, OCCASION_KEYWORDS,
  STYLE_KEYWORDS, MATERIAL_KEYWORDS, KNOWN_BRANDS, LUXURY_BRANDS_LOWER,
};

// ════════════════════════════════════════════════════════════════════════
// v1.12.268 — EXHAUSTIVE_SEARCH : matching multi-champs FR+EN avec scoring
// ════════════════════════════════════════════════════════════════════════
//
// Cherche le query string sur TOUS les champs du produit avec poids :
//   name (100) · category (100) · subcategory (80) · brand (70)
//   style_tags (60) · occasion_tags (60) · ai_keywords (50) · ai_description (50)
//   colors (40) · materials (30) · season_tags (40)
//
// Synonymes FR↔EN auto via les dicts existants (CATEGORY_KEYWORDS, COLOR_KEYWORDS, etc.).

const FIELD_WEIGHTS = {
  name: 100, category: 100, subcategory: 80, brand: 70,
  style_tags: 60, occasion_tags: 60, ai_keywords: 50, ai_description: 50,
  colors: 40, materials: 30, season_tags: 40,
};

// Inverse dict : keyword → canonical (pour expansion FR/EN)
const KW_TO_CANONICAL = (() => {
  const m = new Map();
  const fill = (dict) => {
    for (const [canonical, kws] of Object.entries(dict)) {
      for (const kw of kws) m.set(normalize(kw), canonical);
    }
  };
  fill(CATEGORY_KEYWORDS);
  fill(COLOR_KEYWORDS);
  fill(STYLE_KEYWORDS);
  fill(OCCASION_KEYWORDS);
  fill(MATERIAL_KEYWORDS);
  return m;
})();

// Tous les synonymes d'un mot (FR + EN) en aplati
function expandTerm(term) {
  const n = normalize(term);
  if (!n) return [];
  const canonical = KW_TO_CANONICAL.get(n);
  const out = new Set([n]);
  if (canonical) {
    // Récupère tous les keywords de tous les dicts qui partagent ce canonical
    for (const dict of [CATEGORY_KEYWORDS, COLOR_KEYWORDS, STYLE_KEYWORDS, OCCASION_KEYWORDS, MATERIAL_KEYWORDS]) {
      const kws = dict[canonical];
      if (kws) for (const k of kws) out.add(normalize(k));
    }
  }
  return Array.from(out);
}

// Match : un terme matche-t-il une string de champ ?
function fieldMatchesTerm(fieldVal, expandedTerms) {
  if (!fieldVal) return 0;
  const v = normalize(Array.isArray(fieldVal) ? fieldVal.join(' ') : String(fieldVal));
  if (!v) return 0;
  for (const t of expandedTerms) {
    if (!t) continue;
    if (v === t) return 1.0;             // exact match
    if (v.includes(' ' + t + ' ') || v.startsWith(t + ' ') || v.endsWith(' ' + t)) return 0.95;
    if (v.includes(t)) return 0.8;       // partial
  }
  return 0;
}

/**
 * Recherche exhaustive locale (FR+EN, multi-champs).
 *
 * @param {string} query — texte libre de recherche
 * @param {Array}  products — catalogue à parcourir
 * @param {Object} opts — { gender, minScore=20, limit=1000 }
 * @returns {Array} produits triés par score (avec _searchScore et _matchedFields)
 */
export function exhaustiveSearch(query, products, opts = {}) {
  const { gender, minScore = 20, limit = 1000 } = opts;
  const q = normalize(query || '');
  if (!q || !Array.isArray(products) || products.length === 0) return [];

  // Tokenize (split par espaces, drop stop-words très courts)
  const tokens = q.split(/\s+/).filter(t => t.length >= 2);
  if (tokens.length === 0) return [];

  // Expand chaque token (FR+EN synonymes)
  const expansionByToken = tokens.map(t => expandTerm(t));

  // Score chaque produit
  const scored = [];
  for (const p of products) {
    if (!p) continue;
    // Filter par genre (avant scoring)
    if (gender && gender !== 'N') {
      const pg = lc(p.gender || p.ai_metadata?.gender || '');
      if (pg && pg !== 'unisex' && pg !== gender && pg !== lc(gender)) continue;
    }

    // Construire les champs à matcher
    const fields = {
      name: p.name || '',
      category: p.category || '',
      subcategory: p.subcategory || p.ai_metadata?.subcategory || p.ai_metadata?.detectedType || '',
      brand: p.brand || '',
      style_tags: p.ai_metadata?.styleTags || p.ai_metadata?.style_tags || [],
      occasion_tags: p.ai_metadata?.occasions ? Object.keys(p.ai_metadata.occasions) : (p.ai_metadata?.occasion_tags || []),
      ai_keywords: p.ai_metadata?.keywords || p.ai_metadata?.aiKeywords || [],
      ai_description: p.ai_metadata?.description || p.description || '',
      colors: [p.color, p.ai_metadata?.primaryColor, ...(p.ai_metadata?.secondaryColors || [])].filter(Boolean),
      materials: [p.material, p.ai_metadata?.material].filter(Boolean),
      season_tags: p.ai_metadata?.seasonality || p.ai_metadata?.season_tags || [],
    };

    let score = 0;
    const matchedFields = [];

    // Pour chaque token (tous doivent matcher au moins UN champ pour pertinence forte)
    let tokensWithMatch = 0;
    for (let i = 0; i < tokens.length; i++) {
      const expanded = expansionByToken[i];
      let bestTokenScore = 0;
      let bestField = null;
      for (const [field, weight] of Object.entries(FIELD_WEIGHTS)) {
        const m = fieldMatchesTerm(fields[field], expanded);
        if (m > 0) {
          const fieldScore = m * weight;
          if (fieldScore > bestTokenScore) { bestTokenScore = fieldScore; bestField = field; }
        }
      }
      if (bestTokenScore > 0) {
        score += bestTokenScore;
        tokensWithMatch++;
        if (bestField && !matchedFields.includes(bestField)) matchedFields.push(bestField);
      }
    }

    // Bonus si TOUS les tokens matchent (requête multi-mots cohérente)
    if (tokensWithMatch === tokens.length && tokens.length > 1) score *= 1.3;

    // Bonus features
    if (p.ai_metadata?.isTrendy) score += 5;
    if (p.discount && p.discount > 20) score += 3;

    if (score >= minScore) {
      scored.push({ ...p, _searchScore: Math.round(score), _matchedFields: matchedFields });
    }
  }

  scored.sort((a, b) => (b._searchScore || 0) - (a._searchScore || 0));
  return scored.slice(0, limit);
}
